import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { AWS_CONFIG } from '../config/aws';
import { AddFAQModal, EditFAQModal } from './FAQModals';

// Category options shown in the dropdown. 'general' = no serviceId; '<id>' = service-specific.
// Keep aligned with src/data/services.js HIDDEN_SERVICE_IDS — graphene coating excluded.
const CATEGORIES = [
  { value: 'general', label: '일반 FAQ', serviceId: null },
  { value: 'exterior-only', label: '외부 세차', serviceId: 'exterior-only' },
  { value: 'interior-only', label: '내부 세차', serviceId: 'interior-only' },
  { value: 'premium-wash', label: '내+외 세차', serviceId: 'premium-wash' },
  { value: 'premium-polish', label: '광택', serviceId: 'premium-polish' },
  { value: 'hydro-coating', label: '유막·발수 코팅', serviceId: 'hydro-coating' },
  { value: 'glass-coating-ceramic', label: '유리막 코팅 (세라믹)', serviceId: 'glass-coating-ceramic' },
  { value: 'basic-wash', label: '오토바이 외부세차', serviceId: 'basic-wash' },
];

const numInputStyle = {
  width: 72, padding: '4px 8px',
  borderRadius: 'var(--radius)', border: '1px solid var(--border)',
  background: 'var(--bg)', color: 'var(--text)', fontSize: '0.9rem',
};

function FaqCard({ faq, onEdit, onDelete, onInlineUpdate, deleting, inlineUpdating }) {
  const busy = deleting === faq.faqId || inlineUpdating === faq.faqId;
  return (
    <div className="card" style={{ padding: 20, marginBottom: 16 }}>
      <p style={{ margin: '0 0 6px', fontSize: '0.8rem', color: 'var(--text-dim)' }}>
        ID: {faq.faqId}
      </p>
      <p style={{ margin: '0 0 4px', fontWeight: 600, color: 'var(--text)' }}>
        Q: {faq.question}
      </p>
      <p style={{ margin: '0 0 12px', fontSize: '0.9rem', color: 'var(--text-muted)', whiteSpace: 'pre-wrap' }}>
        A: {faq.answer}
      </p>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>표시 순서:</span>
          <input
            type="number" min="0"
            defaultValue={faq.sortOrder ?? 0}
            disabled={busy}
            onBlur={e => {
              const v = parseInt(e.target.value, 10) || 0;
              if (v !== (faq.sortOrder ?? 0)) onInlineUpdate(faq.faqId, { sortOrder: v });
            }}
            style={numInputStyle}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <input
            id={`vis-${faq.faqId}`} type="checkbox"
            checked={faq.visible !== false} disabled={busy}
            onChange={e => onInlineUpdate(faq.faqId, { visible: e.target.checked })}
            style={{ width: 16, height: 16, cursor: 'pointer' }}
          />
          <label htmlFor={`vis-${faq.faqId}`}
            style={{ fontSize: '0.85rem', color: 'var(--text-muted)', cursor: 'pointer' }}>
            노출
          </label>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button className="btn btn-outline" disabled={busy} onClick={() => onEdit(faq)}
            style={{ padding: '6px 14px', fontSize: '0.85rem' }}>
            편집
          </button>
          <button className="btn btn-outline" disabled={busy} onClick={() => onDelete(faq)}
            style={{ padding: '6px 14px', fontSize: '0.85rem', color: '#ff5252', borderColor: '#ff5252' }}>
            {deleting === faq.faqId ? '삭제 중...' : '삭제'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function FAQManager() {
  const { getIdToken } = useAuth();
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [inlineUpdating, setInlineUpdating] = useState(null);
  const [category, setCategory] = useState('general');

  const currentCategory = CATEGORIES.find(c => c.value === category) || CATEGORIES[0];

  const fetchFaqs = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getIdToken();
      const url = currentCategory.serviceId
        ? `${AWS_CONFIG.apiUrl}/faqs/all?serviceId=${encodeURIComponent(currentCategory.serviceId)}`
        : `${AWS_CONFIG.apiUrl}/faqs/all`;
      const res = await fetch(url, { headers: { Authorization: token } });
      const data = await res.json();
      setFaqs(data.faqs || []);
    } catch (err) {
      console.error('[FAQManager:fetchFaqs] error', err);
    }
    setLoading(false);
  }, [getIdToken, currentCategory.serviceId]);

  useEffect(() => { fetchFaqs(); }, [fetchFaqs]);

  const handleDelete = async (faq) => {
    if (!window.confirm(`"${faq.question}" 질문을 정말 삭제할까요?`)) return;
    setDeleting(faq.faqId);
    try {
      const token = await getIdToken();
      const res = await fetch(`${AWS_CONFIG.apiUrl}/faqs/${faq.faqId}`, {
        method: 'DELETE', headers: { Authorization: token },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        alert(`삭제 실패: ${body.error || res.status}`);
      } else {
        await fetchFaqs();
      }
    } catch (err) {
      console.error('[FAQManager:handleDelete] error', err);
      alert(`서버 오류: ${err.message}`);
    }
    setDeleting(null);
  };

  const handleInlineUpdate = useCallback(async (faqId, patch) => {
    setInlineUpdating(faqId);
    try {
      const token = await getIdToken();
      const target = faqs.find(f => f.faqId === faqId);
      if (!target) return;
      const body = { question: target.question, answer: target.answer, sortOrder: target.sortOrder, visible: target.visible, ...patch };
      const res = await fetch(`${AWS_CONFIG.apiUrl}/faqs/${faqId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: token },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        alert(`갱신 실패: ${data.error || res.status}`);
      } else {
        setFaqs(prev => prev.map(f => f.faqId === faqId ? { ...f, ...patch } : f));
      }
    } catch (err) {
      console.error('[FAQManager:handleInlineUpdate] error', err);
      alert(`서버 오류: ${err.message}`);
    }
    setInlineUpdating(null);
  }, [faqs, getIdToken]);

  return (
    <div>
      <h1>FAQ 관리</h1>

      {/* Category dropdown — switches between general FAQ and service-specific FAQs */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <label style={{ fontSize: '0.9rem', color: 'var(--text-muted)' }}>카테고리:</label>
        <select
          value={category}
          onChange={e => setCategory(e.target.value)}
          style={{
            padding: '8px 12px', borderRadius: 'var(--radius)',
            border: '1px solid var(--border)', background: 'var(--bg)',
            color: 'var(--text)', fontSize: '0.9rem', minWidth: 220,
          }}
        >
          {CATEGORIES.map(c => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>
        <span style={{ fontSize: '0.8rem', color: 'var(--text-dim)' }}>
          {currentCategory.serviceId
            ? `→ /services/${currentCategory.serviceId} 페이지에 노출`
            : '→ /faq 페이지 + 모든 서비스 페이지 공통'}
        </span>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        <button className="btn btn-primary" onClick={() => setShowAddModal(true)}
          style={{ fontSize: '0.85rem', padding: '8px 16px' }}>
          + {currentCategory.label}에 질문 추가
        </button>
      </div>

      {showAddModal && (
        <AddFAQModal
          serviceId={currentCategory.serviceId}
          categoryLabel={currentCategory.label}
          onClose={() => setShowAddModal(false)}
          onCreated={() => { setShowAddModal(false); fetchFaqs(); }}
          getIdToken={getIdToken}
        />
      )}
      {editingFaq && (
        <EditFAQModal
          faq={editingFaq}
          onClose={() => setEditingFaq(null)}
          onSaved={() => { setEditingFaq(null); fetchFaqs(); }}
          getIdToken={getIdToken}
        />
      )}

      {loading ? (
        <p style={{ color: 'var(--text-muted)' }}>로딩 중...</p>
      ) : faqs.length === 0 ? (
        <p style={{ color: 'var(--text-muted)' }}>
          {currentCategory.label}에 등록된 질문이 없습니다. 위에서 추가하세요.
        </p>
      ) : (
        faqs.map(faq => (
          <FaqCard
            key={faq.faqId}
            faq={faq}
            onEdit={setEditingFaq}
            onDelete={handleDelete}
            onInlineUpdate={handleInlineUpdate}
            deleting={deleting}
            inlineUpdating={inlineUpdating}
          />
        ))
      )}
    </div>
  );
}
